import {
  Button,
  CircularProgress,
  MenuList,
  TextField,
  Typography,
} from '@material-ui/core';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { searchConversationOpenings } from '../api';
import { messages } from '../messages';
import {
  ConversationFilterMenu,
  FilterMenuItem,
} from './ConversationFilterMenu';
import { useStyles } from './styles';

export const ConversationOpeningFilter = ({
  accountSlug,
  agentId,
  value,
  onChange,
}: {
  accountSlug: string;
  agentId: string;
  value?: string;
  onChange: (opening?: string) => void;
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [query, setQuery] = useState({ value: '' });
  const [search, setSearch] = useState('');
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );
  const [data, setData] = useState<
    Awaited<ReturnType<typeof searchConversationOpenings>>
  >({ openings: [], seesAllOpenings: false });
  const [titles, setTitles] = useState<Record<string, string>>({});
  const requestId = useRef(0);
  const debouncedSearch = useMemo(
    () => debounce((value: string) => setQuery({ value }), 300),
    [],
  );
  const open = !!anchor;
  const q =
    open && query.value.trim().length >= 2 ? query.value.trim() : undefined;
  const hasOpening = !!value && value !== 'none';

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  useEffect(() => {
    if (!open && !hasOpening) return undefined;
    requestId.current += 1;
    const id = requestId.current;
    setStatus('loading');
    const timeout = setTimeout(() => {
      if (requestId.current !== id) return;
      requestId.current += 1;
      setStatus('error');
    }, 30000);
    void searchConversationOpenings(accountSlug, agentId, q)
      .then(response => {
        if (requestId.current !== id) return;
        setData(response);
        setTitles(previous => ({
          ...previous,
          ...Object.fromEntries(
            response.openings.map(item => [item.externalId, item.title]),
          ),
        }));
        setStatus('ready');
        clearTimeout(timeout);
      })
      .catch(() => {
        if (requestId.current !== id) return;
        setStatus('error');
        clearTimeout(timeout);
      });
    return () => {
      requestId.current += 1;
      clearTimeout(timeout);
    };
  }, [accountSlug, agentId, open, hasOpening, q, query, retry]);

  const close = () => {
    debouncedSearch.cancel();
    setAnchor(null);
    setSearch('');
    setQuery({ value: '' });
  };
  const select = (opening?: string) => {
    onChange(opening);
    close();
  };
  const label = () => {
    if (!value) return intl.formatMessage(messages.filterAllOpenings);
    if (value === 'none') return intl.formatMessage(messages.filterNoOpening);
    return titles[value] ?? intl.formatMessage(messages.unknownOpening);
  };

  return (
    <ConversationFilterMenu
      label={label()}
      name={intl.formatMessage(messages.filterOpening)}
      narrowed={!!value}
      anchor={anchor}
      onOpen={setAnchor}
      onClose={close}
      autoFocus={false}
      dialog
    >
      <div className={classes.openingSearch}>
        <TextField
          id="conversation-opening-search"
          autoFocus
          fullWidth
          label={intl.formatMessage(messages.searchOpenings)}
          value={search}
          onKeyDown={event => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              event.currentTarget
                .closest('[role="dialog"]')
                ?.querySelector<HTMLElement>('[role="menuitem"]')
                ?.focus();
            }
            if (event.key !== 'Escape') event.stopPropagation();
          }}
          onChange={event => {
            const next = event.target.value;
            requestId.current += 1;
            setStatus('loading');
            setSearch(next);
            debouncedSearch(next);
          }}
        />
      </div>
      {status === 'loading' && (
        <div className={classes.menuStatus} role="status">
          <CircularProgress
            size={20}
            aria-label={intl.formatMessage(globalMessages.loading)}
          />
        </div>
      )}
      {status === 'error' && (
        <div className={classes.menuStatus} role="alert">
          <Typography variant="body2">
            {intl.formatMessage(messages.openingsLoadError)}
          </Typography>
          <Button onClick={() => setRetry(previous => previous + 1)}>
            {intl.formatMessage(globalMessages.tryAgain)}
          </Button>
        </div>
      )}
      <MenuList
        disablePadding
        aria-label={intl.formatMessage(messages.filterOpening)}
        onKeyDown={event => {
          if (event.key !== 'Escape') event.stopPropagation();
        }}
      >
        <FilterMenuItem selected={!value} onClick={() => select()}>
          {intl.formatMessage(messages.filterAllOpenings)}
        </FilterMenuItem>
        {status === 'ready' &&
          data.openings.map(opening => (
            <FilterMenuItem
              key={opening.externalId}
              selected={value === opening.externalId}
              onClick={() => select(opening.externalId)}
            >
              {opening.title}
            </FilterMenuItem>
          ))}
        {status === 'ready' && data.seesAllOpenings && (
          <FilterMenuItem
            selected={value === 'none'}
            onClick={() => select('none')}
          >
            {intl.formatMessage(messages.filterNoOpening)}
          </FilterMenuItem>
        )}
      </MenuList>
      {status === 'ready' && !data.openings.length && (
        <Typography variant="body2" className={classes.menuStatus}>
          {intl.formatMessage(messages.noMatchingOpenings)}
        </Typography>
      )}
    </ConversationFilterMenu>
  );
};
